import { lazy, Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { scrollToSection, SECTION_PATHS, pathForSection } from "@/lib/scrollToSection";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import { useActivitiesEnabled } from "@/hooks/useActivitiesEnabled";
import Hero from "@/components/Hero";
import QuickInfo from "@/components/QuickInfo";
import Services from "@/components/Services";
import PageSEO from "@/components/PageSEO";

// Below-the-fold: split into separate chunks loaded on demand
const Fleet = lazy(() => import("@/components/Fleet"));
const Destinations = lazy(() => import("@/components/Destinations"));
const Activities = lazy(() => import("@/components/Activities"));
const ClientGallery = lazy(() => import("@/components/ClientGallery"));
const TransferPricing = lazy(() => import("@/components/TransferPricing"));
const SpecializedServices = lazy(() => import("@/components/SpecializedServices"));
const VipService = lazy(() => import("@/components/VipService"));
const BookingForm = lazy(() => import("@/components/BookingForm"));
const CustomTrip = lazy(() => import("@/components/CustomTrip"));
const EssaouiraGuide = lazy(() => import("@/components/EssaouiraGuide"));
const Testimonials = lazy(() => import("@/components/Testimonials"));
const LatestGuides = lazy(() => import("@/components/LatestGuides"));
const FAQ = lazy(() => import("@/components/FAQ"));
const Footer = lazy(() => import("@/components/Footer"));
const WhatsAppButton = lazy(() => import("@/components/WhatsAppButton"));

const Index = () => {
  const { t } = useTranslation();
  const activitiesEnabled = useActivitiesEnabled();
  const location = useLocation();

  const navigate = useNavigate();

  useEffect(() => {
    if (location.hash && location.hash !== "#") {
      navigate(pathForSection(location.hash), { replace: true });
      return;
    }
    const target = SECTION_PATHS[location.pathname];
    if (target === "#") window.scrollTo({ top: 0, behavior: "smooth" });
    else if (target) scrollToSection(target, target === "#activities" ? 15 : 40, target === "#activities" ? "#destinations" : undefined);
  }, [location.key]);

  return (
    <main>
      <PageSEO
        title={t("seo.homeTitle")}
        description={t("seo.homeDescription")}
        path="/"
      />
      <Navbar />
      <Hero />
      <Services />
      <Suspense fallback={null}>
        <TransferPricing />
        <QuickInfo />
        <Fleet />
        <SpecializedServices />
        <VipService />
        <Destinations />
        {activitiesEnabled && <Activities />}
        <ClientGallery />
        <BookingForm />
        <EssaouiraGuide />
        <CustomTrip />
        <LatestGuides />
        <Testimonials />
        <FAQ />
        <Footer />
        <WhatsAppButton />
      </Suspense>
    </main>
  );
};

export default Index;
